import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, X, ChevronDown, RotateCcw } from "lucide-react";
import formatApiError from "../../lib/apiError";
import useActiveInactive from "../../lib/useActiveInactive";
import ActiveInactiveTabs from "../../components/ActiveInactiveTabs";

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => ({ withCredentials: true });
const BASE = `${API}/api/medimagic-lessons`;

const EMPTY_FORM = { name: "", lesson_code: "", subject_id: "", nmc_lesson_id: "", description: "" };

export default function MedimagicLessonsPage() {
  const { view, setView, list: lessons, inactiveCount, loading, error, reload } = useActiveInactive(BASE);
  const [nmcList, setNmcList]       = useState([]);
  const [subjects, setSubjects]     = useState([]);

  const [modal, setModal]           = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [formErr, setFormErr]       = useState("");
  const [saving, setSaving]         = useState(false);

  const [confirmId, setConfirmId]   = useState(null);
  const [deleting, setDeleting]     = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const filteredNmc = useMemo(
    () => (form.subject_id ? nmcList.filter((n) => n.subject_id === form.subject_id) : []),
    [nmcList, form.subject_id]
  );

  const fetchAux = useCallback(async () => {
    try {
      const [nmcRes, subjectsRes] = await Promise.all([
        axios.get(`${API}/api/nmc-lessons`, ax()),
        axios.get(`${API}/api/subjects`, ax()),
      ]);
      setNmcList(nmcRes.data);
      setSubjects(subjectsRes.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchAux(); }, [fetchAux]);

  const openAdd = () => { setForm(EMPTY_FORM); setFormErr(""); setModal({ mode: "add", lesson: null }); };
  const openEdit = (l) => {
    setForm({ name: l.name, lesson_code: l.lesson_code, subject_id: l.subject_id, nmc_lesson_id: l.nmc_lesson_id, description: l.description });
    setFormErr(""); setModal({ mode: "edit", lesson: l });
  };
  const closeModal = () => { setModal(null); setFormErr(""); };

  const handleSave = async () => {
    if (!form.name.trim())         { setFormErr("Name is required."); return; }
    if (!form.lesson_code.trim())  { setFormErr("Lesson Code is required."); return; }
    if (!form.subject_id)          { setFormErr("Please select a subject."); return; }
    if (!form.nmc_lesson_id)       { setFormErr("Please select an NMC lesson."); return; }
    setSaving(true); setFormErr("");
    try {
      if (modal.mode === "add") await axios.post(BASE, form, ax());
      else                       await axios.put(`${BASE}/${modal.lesson.id}`, form, ax());
      closeModal(); reload();
    } catch (e) { setFormErr(formatApiError(e, "Something went wrong.")); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await axios.delete(`${BASE}/${confirmId}`, ax()); setConfirmId(null); reload(); }
    catch { setConfirmId(null); }
    finally { setDeleting(false); }
  };

  const handleRestore = async (id) => {
    setRestoringId(id);
    try { await axios.post(`${BASE}/${id}/restore`, {}, ax()); reload(); }
    catch (e) { alert(formatApiError(e, "Could not restore.")); }
    finally { setRestoringId(null); }
  };

  return (
    <div className="page-wrap">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Medimagic Lessons</h1>
          <p className="page-sub">Manage Medimagic lesson content mapped to NMC lessons.</p>
        </div>
        {view === "active" && (
          <button className="btn-primary" onClick={openAdd} data-testid="add-mm-lesson-btn">
            <Plus size={16} /> Add Lesson
          </button>
        )}
      </div>

      <ActiveInactiveTabs view={view} onChange={setView} inactiveCount={inactiveCount} testIdPrefix="mm-view" />

      {loading ? <div className="table-loading">Loading…</div>
       : error  ? <div className="table-error">{error}</div>
       : (
        <div className="data-card">
          {lessons.length === 0 ? (
            <div className="empty-state">
              {view === "active"
                ? <p>No Medimagic lessons yet. Click <strong>Add Lesson</strong> to create one.</p>
                : <p>No inactive lessons.</p>}
            </div>
          ) : (
            <table className="data-table" data-testid="mm-lessons-table">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th style={{ width: 130 }}>MM Code</th>
                  <th>Name</th>
                  <th style={{ width: 140 }}>Subject</th>
                  <th style={{ width: 160 }}>NMC Lesson</th>
                  <th>Description</th>
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {lessons.map((l, i) => (
                  <tr key={l.id} data-testid={`mm-lesson-row-${l.id}`}>
                    <td className="td-num">{i + 1}</td>
                    <td><span className="td-badge">{l.lesson_code}</span></td>
                    <td className="td-name">{l.name}</td>
                    <td>
                      <span className="td-badge td-badge-purple">{l.subject_code}</span>
                      <span className="td-nmc-name">{l.subject_name}</span>
                    </td>
                    <td>
                      <span className="td-badge td-badge-teal">{l.nmc_lesson_code}</span>
                      <span className="td-nmc-name">{l.nmc_lesson_name}</span>
                    </td>
                    <td className="td-desc">{l.description || <span className="td-empty">—</span>}</td>
                    <td>
                      <div className="action-btns">
                        {view === "active" ? (
                          <>
                            <button className="action-edit"   onClick={() => openEdit(l)}        data-testid={`edit-mm-lesson-${l.id}`}   title="Edit"><Pencil size={15} /></button>
                            <button className="action-delete" onClick={() => setConfirmId(l.id)} data-testid={`delete-mm-lesson-${l.id}`} title="Delete"><Trash2 size={15} /></button>
                          </>
                        ) : (
                          <button className="action-restore" onClick={() => handleRestore(l.id)} disabled={restoringId === l.id} data-testid={`restore-mm-lesson-${l.id}`} title="Restore"><RotateCcw size={15} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()} data-testid="mm-lesson-modal">
            <div className="modal-head">
              <h2 className="modal-title">{modal.mode === "add" ? "Add Medimagic Lesson" : "Edit Medimagic Lesson"}</h2>
              <button className="modal-close" onClick={closeModal}><X size={18} /></button>
            </div>
            {formErr && <div className="form-error" data-testid="form-error">{formErr}</div>}
            <div className="modal-body">
              <div className="field-row">
                <div className="field" style={{ flex: 2 }}>
                  <label className="field-label">Name <span className="required">*</span></label>
                  <input className="field-input" placeholder="e.g. Heart Anatomy" value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    data-testid="mm-lesson-name-input" maxLength={200} autoFocus />
                </div>
                <div className="field">
                  <label className="field-label">Lesson Code <span className="required">*</span></label>
                  <input className="field-input" placeholder="e.g. MM-CVS-01" value={form.lesson_code}
                    onChange={(e) => setForm((f) => ({ ...f, lesson_code: e.target.value.toUpperCase() }))}
                    data-testid="mm-lesson-code-input" maxLength={50} style={{ textTransform: "uppercase" }} />
                </div>
              </div>
              <div className="field">
                <label className="field-label">Subject <span className="required">*</span></label>
                <div className="select-wrap">
                  <select className="field-input field-select" value={form.subject_id}
                    onChange={(e) => setForm((f) => ({ ...f, subject_id: e.target.value, nmc_lesson_id: "" }))}
                    data-testid="subject-select">
                    <option value="">— Select Subject —</option>
                    {subjects.map((s) => (<option key={s.id} value={s.id}>{s.code} — {s.name}</option>))}
                  </select>
                  <ChevronDown size={16} className="select-chevron" />
                </div>
              </div>
              <div className="field">
                <label className="field-label">Applicable NMC Lesson <span className="required">*</span></label>
                <div className="select-wrap">
                  <select className="field-input field-select" value={form.nmc_lesson_id}
                    onChange={(e) => setForm((f) => ({ ...f, nmc_lesson_id: e.target.value }))}
                    disabled={!form.subject_id} data-testid="nmc-lesson-select">
                    <option value="">{form.subject_id ? "— Select NMC Lesson —" : "— Select a subject first —"}</option>
                    {filteredNmc.map((n) => (<option key={n.id} value={n.id}>{n.lesson_code} — {n.name}</option>))}
                  </select>
                  <ChevronDown size={16} className="select-chevron" />
                </div>
              </div>
              <div className="field">
                <label className="field-label">Description</label>
                <textarea className="field-input field-textarea" placeholder="Brief description of this lesson…"
                  value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  data-testid="mm-lesson-desc-input" maxLength={600} rows={3} />
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="save-mm-lesson-btn">
                {saving ? <span className="btn-spinner" /> : modal.mode === "add" ? "Add Lesson" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmId && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="modal-box modal-box-sm" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm-modal">
            <div className="modal-head">
              <h2 className="modal-title">Delete Lesson</h2>
              <button className="modal-close" onClick={() => setConfirmId(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <p className="confirm-text">Move this lesson to <strong>Inactive</strong>? It will be hidden from selection lists but can be restored later.</p>
            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={() => setConfirmId(null)}>Cancel</button>
              <button className="btn-danger" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-btn">
                {deleting ? <span className="btn-spinner" /> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
